import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, DollarSign, BarChart3, ShoppingCart, Truck, RefreshCw, Printer, Edit2, Save, X, Calendar, History, ArrowLeftRight } from 'lucide-react';
import api from '../api';
import ProductVendorsCard from '../components/ProductVendorsCard';

const fmtCurrency = (v) => v != null && v !== '' ? `$${Number(v).toFixed(2)}` : '$0.00';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [reorderMsg, setReorderMsg] = useState('');
  
  // Cost price editing state
  const [editingCost, setEditingCost] = useState(false);
  const [newCostPrice, setNewCostPrice] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [costHistory, setCostHistory] = useState([]);
  const [showCostHistory, setShowCostHistory] = useState(false);
  const [costSaving, setCostSaving] = useState(false);
  const [_stockMovements, _setStockMovements] = useState([]);

  // Regular price editing state
  const [_editingRegular, setEditingRegular] = useState(false);
  const [newRegularPrice, setNewRegularPrice] = useState('');
  const [regularEffDate, setRegularEffDate] = useState('');
  const [_regularHistory, setRegularHistory] = useState([]);
  const [_showRegularHistory, _setShowRegularHistory] = useState(false);
  const [_regularSaving, setRegularSaving] = useState(false);

  // Vendor editing state
  const [vendors, setVendors] = useState([]);
  const [editingVendor, setEditingVendor] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  // Admin-configured pricing schemes (drive which Price rows are displayed)
  const [pricingSchemes, setPricingSchemes] = useState([]);

  // Category editor state — typeahead autocomplete from product_categories collection
  const [allCategories, setAllCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(false);
  const [categoryInput, setCategoryInput] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  const loadProduct = useCallback(async () => {
    try {
      const [data, vendorList] = await Promise.all([
        api.getProduct(id),
        api.getVendors()
      ]);
      setProduct(data);
      setVendors(Array.isArray(vendorList) ? vendorList : []);
      setEditData({
        quantity_on_hand: data.quantity_on_hand || 0,
        reorder_point: data.reorder_point || 10,
        reorder_quantity: data.reorder_quantity || 50,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  // Load admin-configured pricing schemes once — drives which price rows display
  useEffect(() => {
    api.request('/api/price-schemes')
      .then(s => setPricingSchemes(Array.isArray(s) ? s : []))
      .catch(() => setPricingSchemes([]));
  }, []);

  // Load product categories for the typeahead dropdown
  useEffect(() => {
    api.request('/api/product-categories')
      .then(cats => setAllCategories(Array.isArray(cats) ? cats.map(c => c.name).filter(Boolean) : []))
      .catch(() => setAllCategories([]));
  }, []);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setShowCategoryDropdown(false);
      }
    };
    if (editingCategory) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [editingCategory]);

  // Filtered matches for the category typeahead
  const matchingCategories = categoryInput
    ? allCategories.filter(c => c.toLowerCase().includes(categoryInput.toLowerCase())).slice(0, 10)
    : allCategories.slice(0, 10);

  const saveCategory = async (newCategory) => {
    const trimmed = (newCategory || '').trim();
    if (!trimmed || trimmed === (product.category || '')) {
      setEditingCategory(false);
      return;
    }
    try {
      await api.request(`/api/products/${id}`, { method: 'PUT', body: JSON.stringify({ category: trimmed }) });
      setProduct(prev => ({ ...prev, category: trimmed }));
      // Add to local list if new, so the next edit sees it in the typeahead
      if (!allCategories.includes(trimmed)) setAllCategories(prev => [...prev, trimmed].sort());
      setEditingCategory(false);
      setShowCategoryDropdown(false);
    } catch (err) {
      alert('Failed to update category: ' + err.message);
    }
  };

  // Order-history date-range filter + CSV export state
  const [orderFromDate, setOrderFromDate] = useState('');
  const [orderToDate, setOrderToDate] = useState('');

  // Apply date range filter (orders already arrive sorted newest-first from backend)
  const filteredOrderHistory = (product?.order_history || []).filter((o) => {
    const raw = o.order_date || o.created_at;
    if (!raw) return true;
    const d = new Date(raw);
    if (orderFromDate && d < new Date(orderFromDate)) return false;
    if (orderToDate) {
      const to = new Date(orderToDate);
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }
    return true;
  });

  const exportOrderHistoryCSV = () => {
    const rows = filteredOrderHistory;
    if (!rows.length) { alert('No rows to export for the selected date range.'); return; }
    const header = ['Order #', 'Customer', 'Date', 'Quantity', 'Total', 'Status'];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    for (const o of rows) {
      lines.push([
        o.order_number,
        o.customer_name || '',
        fmtDate(o.order_date || o.created_at),
        o.quantity ?? '',
        Number(o.total || 0).toFixed(2),
        o.status || '',
      ].map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order-history-${product?.sku || id}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Purchase-history date-range filter + CSV (mirror of Order History)
  const [poFromDate, setPoFromDate] = useState('');
  const [poToDate, setPoToDate] = useState('');
  const filteredPoHistory = (product?.po_history || []).filter((po) => {
    const raw = po.order_date || po.created_at;
    if (!raw) return true;
    const d = new Date(raw);
    if (poFromDate && d < new Date(poFromDate)) return false;
    if (poToDate) {
      const to = new Date(poToDate);
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }
    return true;
  });
  const exportPoHistoryCSV = () => {
    const rows = filteredPoHistory;
    if (!rows.length) { alert('No rows to export for the selected date range.'); return; }
    const header = ['PO #', 'Vendor', 'Date', 'Total', 'Status'];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    for (const po of rows) {
      lines.push([
        po.po_number || '',
        po.vendor_name || '',
        fmtDate(po.order_date || po.created_at),
        Number(po.total || 0).toFixed(2),
        po.status || '',
      ].map(esc).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-history-${product?.sku || id}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSaveInventory = async () => {
    try {
      await api.updateProductInventory(id, editData);
      setEditing(false);
      loadProduct();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReorder = async () => {
    try {
      const result = await api.reorderProduct(id);
      setReorderMsg(result.message);
      setTimeout(() => setReorderMsg(''), 5000);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSaveCostPrice = async () => {
    setCostSaving(true);
    try {
      await api.updateCostPrice(id, parseFloat(newCostPrice), effectiveDate);
      setEditingCost(false);
      loadProduct();
      loadCostHistory();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setCostSaving(false);
    }
  };

  const loadCostHistory = async () => {
    try {
      const data = await api.getCostHistory(id);
      setCostHistory(data);
    } catch (err) {
      console.error(err);
    }
  };

  const startEditCost = () => {
    setNewCostPrice(product?.cost_price || 0);
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setEditingCost(true);
    loadCostHistory();
  };

  // Regular price handlers
  const _handleSaveRegularPrice = async () => {
    setRegularSaving(true);
    try {
      await api.updatePrice(id, 'unit_price', parseFloat(newRegularPrice), regularEffDate);
      setEditingRegular(false);
      loadProduct();
      loadRegularHistory();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setRegularSaving(false);
    }
  };

  const loadRegularHistory = async () => {
    try {
      const data = await api.getPriceHistory(id, 'unit_price');
      setRegularHistory(data);
    } catch (err) {
      console.error(err);
    }
  };

  const _startEditRegular = () => {
    setNewRegularPrice(product?.unit_price || 0);
    setRegularEffDate(new Date().toISOString().split('T')[0]);
    setEditingRegular(true);
    loadRegularHistory();
  };

  const startEditVendor = () => {
    setSelectedVendorId(product?.vendor_id || '');
    setEditingVendor(true);
    setShowAddVendor(false);
    setNewVendorName('');
  };

  const handleSaveVendor = async () => {
    try {
      let vendorId = selectedVendorId;
      let vendorName = '';

      if (showAddVendor && newVendorName.trim()) {
        const newVendor = await api.createVendor({ name: newVendorName.trim() });
        vendorId = newVendor.id;
        vendorName = newVendor.name;
        const updated = await api.getVendors();
        setVendors(Array.isArray(updated) ? updated : []);
      } else {
        const v = vendors.find(v => v.id === vendorId);
        vendorName = v ? v.name : '';
      }

      await api.request(`/api/products/${id}/vendor`, {
        method: 'PUT',
        body: JSON.stringify({ vendor_id: vendorId, vendor_name: vendorName, apply_tax_scheme: true })
      });
      setEditingVendor(false);
      loadProduct();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  };

  const handlePrintBarcode = () => {
    const params = new URLSearchParams({ sku: product.sku, name: product.name, description: product.description || '' });
    navigate(`/print-barcode?${params.toString()}`);
  };

  // Single "Save All Open Edits" button — flushes whatever editors are currently open.
  // Each editor keeps its own per-card Save button too; this is the umbrella flush.
  const [savingAll, setSavingAll] = useState(false);
  const handleSaveAll = async () => {
    setSavingAll(true);
    const tasks = [];
    if (editing) tasks.push(handleSaveInventory());
    if (editingCost) tasks.push(handleSaveCostPrice());
    if (editingVendor) tasks.push(handleSaveVendor());
    if (editingCategory && categoryInput && categoryInput !== (product?.category || '')) {
      tasks.push(saveCategory(categoryInput));
    }
    try {
      await Promise.all(tasks);
    } finally {
      setSavingAll(false);
    }
  };
  const anyEditorOpen = editing || editingCost || editingVendor || editingCategory;

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  if (!product) return <div className="p-8 text-center text-red-500">Product not found</div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Package },
    { id: 'orders', label: 'Order History', icon: ShoppingCart },
    { id: 'purchases', label: 'Purchase History', icon: Truck },
    { id: 'movements', label: 'Stock Movements', icon: ArrowLeftRight },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="product-detail">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-md hover:bg-muted" data-testid="back-to-products">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground" data-testid="product-name">{product.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                <span>SKU: <strong>{product.sku}</strong></span>
                {/* Editable category with typeahead (R-feature): click to edit, autocompletes from product_categories */}
                <span className="relative" ref={categoryDropdownRef}>
                  {!editingCategory ? (
                    <button
                      type="button"
                      onClick={() => { setCategoryInput(product.category || ''); setEditingCategory(true); setShowCategoryDropdown(true); }}
                      className="hover:underline decoration-dotted underline-offset-4"
                      data-testid="category-edit-btn"
                      title="Click to change category"
                    >
                      Category: <strong>{product.category || 'Uncategorized'}</strong>
                    </button>
                  ) : (
                    <>
                      <input
                        autoFocus
                        type="text"
                        value={categoryInput}
                        onChange={(e) => { setCategoryInput(e.target.value); setShowCategoryDropdown(true); }}
                        onFocus={() => setShowCategoryDropdown(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveCategory(categoryInput); }
                          if (e.key === 'Escape') { setEditingCategory(false); setShowCategoryDropdown(false); }
                        }}
                        onBlur={() => setTimeout(() => { if (editingCategory) saveCategory(categoryInput); }, 200)}
                        placeholder="Type or choose a category..."
                        className="px-2 py-0.5 border border-input rounded-md text-sm w-48"
                        data-testid="category-input"
                      />
                      {showCategoryDropdown && matchingCategories.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-56 overflow-y-auto w-56" data-testid="category-dropdown">
                          {matchingCategories.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => saveCategory(c)}
                              className="w-full text-left px-3 py-1.5 hover:bg-muted transition-colors text-sm"
                              data-testid={`category-option-${c}`}
                            >
                              {c}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </span>
                {product.barcode && <span>Barcode: <strong>{product.barcode}</strong></span>}
                <button
                  onClick={async () => {
                    try {
                      const newVal = product.is_taxable === false;
                      await api.request(`/api/products/${id}/taxable`, { method: 'PUT', body: JSON.stringify({ is_taxable: newVal }) });
                      setProduct(prev => ({ ...prev, is_taxable: newVal }));
                    } catch (err) { alert('Failed to update: ' + err.message); }
                  }}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${product.is_taxable !== false ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  title="PWO-only flag — all other customers always pay tax on this product (R6)"
                  data-testid="toggle-taxable">
                  {product.is_taxable !== false ? 'PWO Taxable' : 'PWO Non-Taxable'}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {anyEditorOpen && (
                <button
                  onClick={handleSaveAll}
                  disabled={savingAll}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50"
                  data-testid="save-all-edits-btn"
                  title="Save every open editor on this page in one shot"
                >
                  <Save className="h-4 w-4" /> {savingAll ? 'Saving…' : 'Save All Changes'}
                </button>
              )}
              <button onClick={handlePrintBarcode} className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm" data-testid="print-barcode-btn">
                <Printer className="h-4 w-4" /> Print Barcode
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Inventory & Location */}
            <div className="lg:col-span-2 space-y-6">
              {/* Inventory Card */}
              <div className="bg-card border rounded-xl p-5" data-testid="inventory-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5 text-primary" /> Inventory</h2>
                  {!editing ? (
                    <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-sm text-primary hover:underline" data-testid="edit-inventory-btn">
                      <Edit2 className="h-3.5 w-3.5" /> Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={handleSaveInventory} className="flex items-center gap-1 text-sm text-green-600 hover:underline" data-testid="save-inventory-btn">
                        <Save className="h-3.5 w-3.5" /> Save
                      </button>
                      <button onClick={() => setEditing(false)} className="flex items-center gap-1 text-sm text-muted-foreground hover:underline">
                        <X className="h-3.5 w-3.5" /> Cancel
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <div className="text-xs text-muted-foreground mb-1">Quantity On Hand</div>
                    {editing ? (
                      <input type="number" value={editData.quantity_on_hand} onChange={e => setEditData({...editData, quantity_on_hand: parseInt(e.target.value) || 0})}
                        className="w-full text-center text-2xl font-bold bg-background border rounded px-2 py-1" data-testid="qty-input" />
                    ) : (
                      <div className={`text-2xl font-bold ${(product.quantity_on_hand || 0) === 0 ? 'text-red-500' : (product.quantity_on_hand || 0) <= 10 ? 'text-amber-600' : 'text-green-700'}`}
                        data-testid="qty-on-hand">{product.quantity_on_hand || 0}</div>
                    )}
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <div className="text-xs text-muted-foreground mb-1">Reorder Point</div>
                    {editing ? (
                      <input type="number" value={editData.reorder_point} onChange={e => setEditData({...editData, reorder_point: parseInt(e.target.value) || 0})}
                        className="w-full text-center text-2xl font-bold bg-background border rounded px-2 py-1" />
                    ) : (
                      <div className="text-2xl font-bold text-foreground">{product.reorder_point || 10}</div>
                    )}
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4 text-center">
                    <div className="text-xs text-muted-foreground mb-1">Reorder Qty</div>
                    {editing ? (
                      <input type="number" value={editData.reorder_quantity} onChange={e => setEditData({...editData, reorder_quantity: parseInt(e.target.value) || 0})}
                        className="w-full text-center text-2xl font-bold bg-background border rounded px-2 py-1" />
                    ) : (
                      <div className="text-2xl font-bold text-foreground">{product.reorder_quantity || 50}</div>
                    )}
                  </div>
                </div>
                {/* Reorder Button */}
                {product.last_vendor && (
                  <div className="mt-4 pt-4 border-t">
                    <button onClick={handleReorder}
                      className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
                      data-testid="reorder-btn">
                      <RefreshCw className="h-4 w-4" /> Reorder from {product.last_vendor}
                    </button>
                    {reorderMsg && <p className="mt-2 text-sm text-green-600" data-testid="reorder-msg">{reorderMsg}</p>}
                  </div>
                )}
              </div>

              {/* Multi-vendor card — one row per vendor with cost + preferred toggle */}
              <ProductVendorsCard productId={id} />

              {/* Product Details Card */}
              <div className="bg-card border rounded-xl p-5">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-4"><Package className="h-5 w-5 text-primary" /> Product Details</h2>
                
                {/* Vendor Assignment */}
                <div className="mb-4 pb-4 border-b">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium flex items-center gap-1"><Truck className="h-4 w-4" /> Vendor</span>
                    {!editingVendor ? (
                      <button onClick={startEditVendor} className="text-primary hover:underline text-xs flex items-center gap-1" data-testid="edit-vendor-btn">
                        <Edit2 className="h-3 w-3" /> Edit
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={handleSaveVendor} className="text-green-600 hover:underline text-xs flex items-center gap-1" data-testid="save-vendor-btn">
                          <Save className="h-3 w-3" /> Save
                        </button>
                        <button onClick={() => setEditingVendor(false)} className="text-muted-foreground hover:underline text-xs flex items-center gap-1">
                          <X className="h-3 w-3" /> Cancel
                        </button>
                      </div>
                    )}
                  </div>
                  {editingVendor ? (
                    <div className="space-y-2">
                      {!showAddVendor ? (
                        <>
                          <select value={selectedVendorId} onChange={e => { if (e.target.value === '__new__') { setShowAddVendor(true); } else { setSelectedVendorId(e.target.value); }}}
                            className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="vendor-select">
                            <option value="">No vendor assigned</option>
                            {vendors.map(v => <option key={v.id} value={v.id}>{v.name} {v.tax_scheme === 'no_tax' ? '(No Tax)' : '(Sales Tax)'}</option>)}
                            <option value="__new__">+ Add New Vendor</option>
                          </select>
                          {selectedVendorId && (() => { const v = vendors.find(x => x.id === selectedVendorId); return v ? (
                            <p className="text-xs text-muted-foreground">Tax scheme: <strong>{v.tax_scheme === 'no_tax' ? 'No Tax' : 'Sales Tax'}</strong> — will be applied to this product</p>
                          ) : null; })()}
                        </>
                      ) : (
                        <div className="flex gap-2">
                          <input type="text" placeholder="New vendor name" value={newVendorName} onChange={e => setNewVendorName(e.target.value)}
                            className="flex-1 px-3 py-2 border border-input rounded-md text-sm" data-testid="new-vendor-name" />
                          <button onClick={() => setShowAddVendor(false)} className="px-2 py-1 text-xs border rounded hover:bg-muted">Back</button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm">
                      {product.last_vendor ? (
                        <span className="font-medium">{product.last_vendor}</span>
                      ) : (
                        <span className="text-muted-foreground">No vendor assigned</span>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                  {[
                    ['Description', product.description],
                    ['Item Type', product.item_type],
                    ['UOM', product.uom],
                    ['Sales UOM', product.sales_uom],
                    ['Purchasing UOM', product.purchasing_uom],
                    ['Vendor Code', product.vendor_product_code],
                    ['Active', product.is_active !== false ? 'Yes' : 'No'],
                    ['HS Code', product.hs_code],
                    ['Country of Origin', product.country_of_origin],
                    ['Remarks', product.remarks],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex justify-between py-1 border-b border-muted/50">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-medium text-foreground text-right">{value}</span>
                    </div>
                  ))}
                  {/* Dimensions */}
                  {(product.product_length || product.product_width || product.product_height || product.product_weight) && (
                    <div className="col-span-2 flex justify-between py-1 border-b border-muted/50">
                      <span className="text-muted-foreground">Dimensions (L x W x H) / Weight</span>
                      <span className="font-medium">{product.product_length || '-'} x {product.product_width || '-'} x {product.product_height || '-'} / {product.product_weight || '-'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Pricing & Cost */}
            <div className="space-y-6">
              <div className="bg-card border rounded-xl p-5" data-testid="pricing-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2"><DollarSign className="h-5 w-5 text-primary" /> Pricing & Cost</h2>
                </div>
                
                {/* Cost Price Editor */}
                {editingCost && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="cost-editor">
                    <h3 className="text-sm font-semibold mb-3">Update Cost Price</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">New Cost Price ($)</label>
                        <input type="number" step="0.01" value={newCostPrice}
                          onChange={e => setNewCostPrice(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm bg-white"
                          data-testid="cost-price-input" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Effective Date</label>
                        <input type="date" value={effectiveDate}
                          onChange={e => setEffectiveDate(e.target.value)}
                          className="w-full border rounded px-3 py-2 text-sm bg-white"
                          data-testid="effective-date-input" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleSaveCostPrice} disabled={costSaving}
                        className="flex items-center gap-1 px-3 py-1.5 bg-primary text-white rounded text-sm hover:bg-primary/90 disabled:opacity-50"
                        data-testid="save-cost-btn">
                        <Save className="h-3.5 w-3.5" /> {costSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={() => setEditingCost(false)}
                        className="flex items-center gap-1 px-3 py-1.5 border rounded text-sm hover:bg-muted">
                        <X className="h-3.5 w-3.5" /> Cancel
                      </button>
                      <button onClick={() => setShowCostHistory(!showCostHistory)}
                        className="flex items-center gap-1 px-3 py-1.5 border rounded text-sm hover:bg-muted ml-auto"
                        data-testid="toggle-cost-history">
                        <History className="h-3.5 w-3.5" /> History
                      </button>
                    </div>
                    
                    {/* Cost History */}
                    {showCostHistory && costHistory.length > 0 && (
                      <div className="mt-3 border-t pt-3 max-h-48 overflow-y-auto" data-testid="cost-history">
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Cost Price History</h4>
                        {costHistory.map((h, i) => (
                          <div key={i} className="flex justify-between items-center py-1.5 text-xs border-b border-muted/50">
                            <div>
                              <span className="text-red-500 line-through mr-1">${(h.old_price ?? h.old_cost_price ?? 0).toFixed(2)}</span>
                              <span className="text-green-600 font-semibold">${(h.new_price ?? h.new_cost_price ?? 0).toFixed(2)}</span>
                            </div>
                            <div className="text-right text-muted-foreground">
                              <div className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Effective: {fmtDate(h.effective_date)}</div>
                              <div>by {h.changed_by_name || h.changed_by} on {fmtDate(h.changed_at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCostHistory && costHistory.length === 0 && (
                      <p className="mt-3 text-xs text-muted-foreground text-center py-2">No cost price changes recorded yet</p>
                    )}
                  </div>
                )}

                <div className="space-y-3 text-sm">
                  {/* Cost Price row with edit button */}
                  <div className="flex justify-between items-center py-2 border-b border-muted/50">
                    <span className="text-muted-foreground">Cost Price</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">{fmtCurrency(product.cost_price)}</span>
                      {!editingCost && (
                        <button onClick={startEditCost} className="text-primary hover:underline" data-testid="edit-cost-btn">
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {/* PWO Price row (read-only) — always equals cost_price (Patrick Webb Oil at-cost) */}
                  {pricingSchemes.some(s => s.name === 'pwo') && (
                    <div className="flex justify-between items-center py-2 border-b border-muted/50">
                      <span className="text-muted-foreground">PWO Price</span>
                      <span className="font-semibold text-amber-700" data-testid="pwo-price-value">{fmtCurrency(product.pwo_price ?? product.cost_price)}</span>
                    </div>
                  )}
                  {/* Regular Price row — only when admin has configured 'regular' scheme */}
                  {pricingSchemes.some(s => s.name === 'regular') && (
                    <div className="flex justify-between items-center py-2 border-b border-muted/50">
                      <span className="text-muted-foreground">Regular Price</span>
                      <span className="font-semibold text-foreground" data-testid="regular-price-value">{fmtCurrency(product.normal_price ?? product.unit_price)}</span>
                    </div>
                  )}
                  {/* Any custom admin-configured schemes (excluding regular & pwo, already shown above) */}
                  {pricingSchemes
                    .filter(s => s.name !== 'pwo' && s.name !== 'regular')
                    .map(s => {
                      const fieldKey = `${s.name}_price`;
                      const value = product[fieldKey];
                      if (value == null) return null;
                      return (
                        <div key={s.name} className="flex justify-between items-center py-2 border-b border-muted/50">
                          <span className="text-muted-foreground">{s.label}</span>
                          <span className="font-semibold text-foreground" data-testid={`${s.name}-price-value`}>{fmtCurrency(value)}</span>
                        </div>
                      );
                    })}
                  {/* Static info fields (NOT pricing schemes — vendor cost + markup metadata) */}
                  {[
                    ['Vendor Price', fmtCurrency(product.vendor_price)],
                    ['Markup', product.markup ? `${product.markup}%` : null],
                    ['Fixed Markup', product.is_fixed_markup ? 'Yes' : null],
                    ['Tax Inclusive', product.is_tax_inclusive ? 'Yes' : null],
                    ['Special Tax Rate', product.special_tax_rate ? `${product.special_tax_rate}%` : null],
                  ].filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} className="flex justify-between py-2 border-b border-muted/50">
                      <span className="text-muted-foreground">{label}</span>
                      <span className="font-semibold text-foreground">{value}</span>
                    </div>
                  ))}

                  {/* PWO Taxable Toggle — R6: Only Patrick Webb Oil, LLC has the per-product taxable choice.
                      All other customers ALWAYS pay tax on every product. */}
                  <div className="flex justify-between items-center py-3 mt-1">
                    <span className="text-muted-foreground font-medium" title="Only Patrick Webb Oil, LLC respects this flag — all other customers always pay tax on this product.">
                      PWO Taxable <span className="text-xs text-muted-foreground/60">(Patrick Webb Oil only)</span>
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          const newVal = product.is_taxable === false;
                          await api.request(`/api/products/${id}/taxable`, { method: 'PUT', body: JSON.stringify({ is_taxable: newVal }) });
                          setProduct(prev => ({ ...prev, is_taxable: newVal }));
                        } catch (err) { alert('Failed to update: ' + err.message); }
                      }}
                      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none ${product.is_taxable !== false ? 'bg-blue-500' : 'bg-gray-300'}`}
                      data-testid="taxable-toggle">
                      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${product.is_taxable !== false ? 'translate-x-8' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-card border rounded-xl p-5">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-4"><MapPin className="h-5 w-5 text-primary" /> Location</h2>
                <div className="text-sm text-muted-foreground">Default Location</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="bg-card border rounded-xl" data-testid="order-history">
            <div className="p-5 border-b flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Sales Order History</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {filteredOrderHistory.length} of {(product.order_history || []).length} orders
                  {(orderFromDate || orderToDate) && ' (filtered)'} — newest first
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-0.5">From</label>
                  <input type="date" value={orderFromDate} onChange={(e) => setOrderFromDate(e.target.value)}
                    className="px-2 py-1.5 border border-input rounded-md text-sm"
                    data-testid="order-from-date" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-0.5">To</label>
                  <input type="date" value={orderToDate} onChange={(e) => setOrderToDate(e.target.value)}
                    className="px-2 py-1.5 border border-input rounded-md text-sm"
                    data-testid="order-to-date" />
                </div>
                {(orderFromDate || orderToDate) && (
                  <button type="button" onClick={() => { setOrderFromDate(''); setOrderToDate(''); }}
                    className="px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded-md"
                    data-testid="order-clear-dates-btn">
                    Clear
                  </button>
                )}
                <button type="button" onClick={exportOrderHistoryCSV}
                  className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary/90"
                  data-testid="order-export-csv-btn">
                  Export CSV
                </button>
              </div>
            </div>
            {filteredOrderHistory.length > 0 ? (
              <table className="w-full">
                <thead><tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Order #</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Customer</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Date ↓</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Qty</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Total</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Status</th>
                </tr></thead>
                <tbody>
                  {filteredOrderHistory.map((o, i) => (
                    <tr key={i} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/sales-orders/${o.id}`)}>
                      <td className="px-4 py-3 text-sm font-medium text-primary">{o.order_number}</td>
                      <td className="px-4 py-3 text-sm">{o.customer_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{fmtDate(o.order_date || o.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-right">{o.quantity ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{fmtCurrency(o.total)}</td>
                      <td className="px-4 py-3 text-sm capitalize">{o.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                {(product.order_history || []).length === 0
                  ? 'No sales order history for this product'
                  : 'No orders match the selected date range — try widening it or clearing the filter.'}
              </div>
            )}
          </div>
        )}

        {activeTab === 'purchases' && (
          <div className="bg-card border rounded-xl" data-testid="purchase-history">
            <div className="p-5 border-b flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Purchase Order History</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {filteredPoHistory.length} of {(product.po_history || []).length} POs
                  {(poFromDate || poToDate) && ' (filtered)'} — newest first
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <label className="block text-xs text-muted-foreground mb-0.5">From</label>
                  <input type="date" value={poFromDate} onChange={(e) => setPoFromDate(e.target.value)}
                    className="px-2 py-1.5 border border-input rounded-md text-sm" data-testid="po-from-date" />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-0.5">To</label>
                  <input type="date" value={poToDate} onChange={(e) => setPoToDate(e.target.value)}
                    className="px-2 py-1.5 border border-input rounded-md text-sm" data-testid="po-to-date" />
                </div>
                {(poFromDate || poToDate) && (
                  <button type="button" onClick={() => { setPoFromDate(''); setPoToDate(''); }}
                    className="px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded-md"
                    data-testid="po-clear-dates-btn">Clear</button>
                )}
                <button type="button" onClick={exportPoHistoryCSV}
                  className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary/90"
                  data-testid="po-export-csv-btn">Export CSV</button>
              </div>
            </div>
            {filteredPoHistory.length > 0 ? (
              <table className="w-full">
                <thead><tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">PO #</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Vendor</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Date ↓</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Total</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Status</th>
                </tr></thead>
                <tbody>
                  {filteredPoHistory.map((po, i) => (
                    <tr key={i} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/purchase-orders/${po.id}`)}>
                      <td className="px-4 py-3 text-sm font-medium text-primary">{po.po_number}</td>
                      <td className="px-4 py-3 text-sm">{po.vendor_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{fmtDate(po.order_date || po.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium">{fmtCurrency(po.total)}</td>
                      <td className="px-4 py-3 text-sm capitalize">{po.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                {(product.po_history || []).length === 0
                  ? 'No purchase order history for this product'
                  : 'No POs match the selected date range — try widening it or clearing the filter.'}
              </div>
            )}
          </div>
        )}

        {activeTab === 'movements' && (
          <StockMovementsTab productId={id} />
        )}
      </div>
    </div>
  );
}

function StockMovementsTab({ productId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await api.getStockMovements(productId);
        setData(result);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productId]);

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading movements...</div>;

  const movements = data?.movements || [];
  const summary = data?.summary || {};

  return (
    <div className="bg-card border rounded-xl" data-testid="stock-movements-tab">
      <div className="p-5 border-b flex items-center justify-between">
        <h2 className="text-lg font-semibold">Stock Movement History</h2>
        <div className="flex gap-4 text-sm">
          <span className="text-green-600 font-medium">In: +{summary.total_in || 0}</span>
          <span className="text-red-600 font-medium">Out: -{summary.total_out || 0}</span>
          <span className="font-medium">Current: {summary.current_stock || 0}</span>
        </div>
      </div>
      {movements.length > 0 ? (
        <table className="w-full">
          <thead><tr className="border-b bg-muted/30">
            <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Date</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Type</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Change</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Balance</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Reference</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">User</th>
          </tr></thead>
          <tbody>
            {movements.map((m, i) => (
              <tr key={i} className="border-b hover:bg-muted/20">
                <td className="px-4 py-2 text-sm text-muted-foreground">
                  {m.timestamp ? new Date(m.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                </td>
                <td className="px-4 py-2 text-sm">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${m.quantity_change > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {m.movement_type === 'sale' ? 'Sale' : m.movement_type === 'po_receive' ? 'PO Received' : m.movement_type === 'adjustment_in' ? 'Adj (+)' : m.movement_type === 'adjustment_out' ? 'Adj (-)' : m.movement_type}
                  </span>
                </td>
                <td className={`px-4 py-2 text-sm text-right font-bold ${m.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {m.quantity_change > 0 ? '+' : ''}{m.quantity_change}
                </td>
                <td className="px-4 py-2 text-sm text-right">{m.balance_after}</td>
                <td className="px-4 py-2 text-sm text-muted-foreground">{m.reference_number || m.notes || '-'}</td>
                <td className="px-4 py-2 text-sm text-muted-foreground">{m.user_email || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="p-8 text-center text-muted-foreground">
          No stock movements recorded yet. Movements are tracked when POs are received, sales orders completed, or manual adjustments made.
        </div>
      )}
    </div>
  );
}
